import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Primitives";
import { api, euro, formatApiError } from "@/lib/api";
import { waLink } from "@/lib/site";
import { WhatsAppIcon } from "@/components/site/BrandIcons";

const PAY_LABEL = { paid: "Paid online", pay_driver: "Pay the driver (cash or card)", pending: "Online payment pending", refunded: "Refunded" };

const Shell = ({ children, testId }) => (
  <section className="container-x pb-24 pt-32 md:pt-40" data-testid={testId}>
    <Seo title="Booking Confirmation" description="Your Alpha Transfer booking." noindex />
    <Reveal className="mx-auto max-w-2xl">{children}</Reveal>
  </section>
);

const Detail = ({ k, v, testId }) => (
  <div className="flex justify-between gap-6 border-b border-slate-100 py-3 text-sm last:border-0">
    <span className="text-slate-500">{k}</span>
    <span className="text-right text-ink" data-testid={testId}>{v}</span>
  </div>
);

export function BookingConfirmation() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch((e) => setError(formatApiError(e)));
  }, [id]);
  if (error) return <Shell testId="confirmation-error"><p className="text-slate-600">{error}</p></Shell>;
  if (!b) return <Shell testId="confirmation-loading"><Loader2 className="h-6 w-6 animate-spin text-gold" /></Shell>;
  return (
    <Shell testId="booking-confirmation">
      <CheckCircle2 className="h-12 w-12 text-gold" strokeWidth={1.2} />
      <span className="eyebrow mt-8">Booking received</span>
      <h1 className="heading-lg mt-4 text-ink">Thank you, {b.name.split(" ")[0]}. We will be waiting for you.</h1>
      <p className="mt-4 text-slate-600">A confirmation email is on its way. Your reference is <strong className="text-ink" data-testid="confirmation-ref">{b.ref}</strong>.</p>
      <div className="card-soft mt-10 border border-slate-200 bg-white p-6 md:p-8">
        <Detail k="From" v={`${b.pickup}${b.pickup_details ? `, ${b.pickup_details}` : ""}`} />
        <Detail k="To" v={`${b.dropoff}${b.dropoff_details ? `, ${b.dropoff_details}` : ""}`} />
        <Detail k="Date & time" v={`${b.date} at ${b.time}`} />
        {b.flight_number && <Detail k="Flight / ferry" v={b.flight_number} />}
        <Detail k="Passengers" v={b.passengers} />
        <Detail k="Price" v={euro(b.price)} testId="confirmation-price" />
        <Detail k="Payment" v={PAY_LABEL[b.payment_status] || b.payment_status} testId="confirmation-payment" />
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/" className="btn btn-dark" data-testid="confirmation-home-button">Back to home</Link>
        <a href={waLink(`Hello Alpha Transfer, about my booking ${b.ref}.`)} target="_blank" rel="noreferrer" className="btn btn-outline" data-testid="confirmation-whatsapp-button">
          <WhatsAppIcon className="h-4 w-4" /> Questions? WhatsApp us
        </a>
      </div>
    </Shell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking");

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return navigate(`/booking/confirmation/${bookingId}`, { replace: true });
        if (data.status === "expired") return setState("failed");
      } catch (e) {
        return setState("failed");
      }
      attempts += 1;
      if (attempts >= 10) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll();
    else setState("failed");
    return () => clearTimeout(timer);
  }, [sessionId, bookingId, navigate]);

  return (
    <Shell testId="payment-success-page">
      {state === "checking" && (
        <div className="flex items-center gap-4" data-testid="payment-checking">
          <Loader2 className="h-6 w-6 animate-spin text-gold" />
          <p className="font-serif text-3xl text-ink">Confirming your payment…</p>
        </div>
      )}
      {state !== "checking" && (
        <div data-testid="payment-status-unknown">
          <h1 className="heading-lg text-ink">{state === "timeout" ? "Payment is still processing" : "We could not confirm your payment"}</h1>
          <p className="mt-4 text-slate-600">Please check your email shortly, or contact us on WhatsApp and we will help right away.</p>
          <a href={waLink("Hello Alpha Transfer, I just tried to pay for my booking online.")} target="_blank" rel="noreferrer" className="btn btn-gold mt-8">WhatsApp us</a>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = params.get("booking");
  const [busy, setBusy] = useState(false);
  const payDriver = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/pay-driver`);
      navigate(`/booking/confirmation/${bookingId}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };
  return (
    <Shell testId="payment-cancel-page">
      <XCircle className="h-12 w-12 text-slate-400" strokeWidth={1.2} />
      <h1 className="heading-lg mt-6 text-ink">Payment not completed</h1>
      <p className="mt-4 text-slate-600">No money was taken. You can keep your booking and simply pay the driver on the day, or start again.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        {bookingId && (
          <button onClick={payDriver} disabled={busy} className="btn btn-gold" data-testid="cancel-pay-driver-button">
            {busy ? "Saving…" : "Keep booking, pay the driver"}
          </button>
        )}
        <Link to="/booking" className="btn btn-outline" data-testid="cancel-restart-button">Start a new booking</Link>
      </div>
    </Shell>
  );
}
